import { hostFetch } from '@bridge/app-sdk'
import { ApiError, api } from '@web/api/client'
import type {
  AppDefinition,
  BridgeConfig,
  SessionConfig,
  SessionRecord,
} from '@web/api/types'
import {
  launchSelection,
  launchSettings,
  ProviderSettings,
} from '@web/components/ProviderSettings'
import { SessionCost } from '@web/components/SessionCost'
import { Icon } from '@web/components/ui/icons'
import { appIcon } from '@web/lib/appIcon'
import { useDemoScenarios } from '@web/state/demo-scenarios'
import { useIdentity } from '@web/state/identity'
import { useEffect, useId, useRef, useState } from 'react'
import '@web/components/chronicle-startup.css'

type Catalog = {
  user: { id: string; name: string; github_login: string }
  organization: { id: string; name: string; slug: string }
  accounts: { id: string; login: string; name: string; connected: boolean }[]
  repositories: {
    id: string
    account_id: string
    name: string
    full_name: string
    description: string
    language: string
    commits: number
    default_branch: string
  }[]
}
type LaunchAttempt = {
  requestId: string
  packageDigest?: string
  draft: true
  project?: string
  environment?: string
  config: Partial<SessionConfig>
  sessionId?: string
}

const CHRONICLE_LAUNCH_SETTINGS = new Set<keyof SessionConfig>([
  'model',
  'effort',
  'fast_mode',
  'idle_timeout_minutes',
  'startup_step',
  'startup_values',
  'permission_mode',
  'max_turns',
  'max_budget_usd',
  'schedule_id',
  'provider',
  'protocol',
  'acp_version',
  'execution_mode',
  'acp_auth_method',
  'acp_config_options',
])

/** Keep a stored retry compatible with the bounded Chronicle launch contract. */
function chronicleLaunchSettings(
  settings: Partial<SessionConfig>,
): Partial<SessionConfig> {
  return Object.fromEntries(
    Object.entries(settings).filter(([key]) =>
      CHRONICLE_LAUNCH_SETTINGS.has(key as keyof SessionConfig),
    ),
  ) as Partial<SessionConfig>
}

function GitHubMark({ size = 24 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 .9a11.1 11.1 0 0 0-3.51 21.63c.56.1.76-.24.76-.54v-2.1c-3.1.67-3.76-1.31-3.76-1.31-.51-1.29-1.24-1.63-1.24-1.63-1.01-.69.08-.68.08-.68 1.12.08 1.71 1.14 1.71 1.14.99 1.69 2.6 1.2 3.23.92.1-.72.39-1.21.71-1.49-2.47-.28-5.07-1.24-5.07-5.49 0-1.21.43-2.2 1.14-2.98-.11-.28-.5-1.41.11-2.94 0 0 .93-.3 3.05 1.14a10.6 10.6 0 0 1 5.56 0c2.12-1.44 3.05-1.14 3.05-1.14.61 1.53.22 2.66.11 2.94.71.78 1.14 1.77 1.14 2.98 0 4.26-2.6 5.2-5.08 5.48.4.35.75 1.02.75 2.06v3.1c0 .3.2.65.76.54A11.1 11.1 0 0 0 12 .9Z" />
    </svg>
  )
}

/** Connect a selected GitHub organization before creating an analysis session. */
export function ChronicleStartup({
  app,
  config,
  record,
  onCreated,
  onStarting,
  onChanged,
  startupKey = 'default',
}: {
  app: AppDefinition
  config: BridgeConfig
  record?: SessionRecord
  onCreated: (record: SessionRecord) => void
  onStarting?: (record: SessionRecord, start: () => Promise<void>) => void
  onChanged: () => void
  startupKey?: string
}) {
  const identity = useIdentity()
  const scenarios = useDemoScenarios(
    app.id,
    record?.config.package_digest ?? app.digest,
    true,
  )
  const formId = useId()
  const [catalog, setCatalog] = useState<Catalog | null>(null)
  const [githubConnected, setGitHubConnected] = useState(false)
  const [accounts, setAccounts] = useState<string[]>([])
  const [draft, setDraft] = useState(record)
  const [providerSettings, setProviderSettings] = useState<
    Partial<SessionConfig>
  >({
    ...config.defaults,
    protocol: 'acp',
    acp_version: 2,
    provider: 'claude',
    ...record?.config,
  })
  const selection = launchSelection(config, providerSettings)
  const [scenario, setScenario] = useState<string | null>(null)
  const [timeout, setTimeoutMinutes] = useState(
    record?.config.idle_timeout_minutes ?? 30,
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [attempt, setAttempt] = useState(0)
  const [launchPending, setLaunchPending] = useState(false)
  const pendingLaunch = useRef<LaunchAttempt | null>(null)
  const storageKey = useRef('')
  const alive = useRef(true)
  const saveAttempt = (value: LaunchAttempt | null) => {
    pendingLaunch.current = value
    setLaunchPending(!!value)
    try {
      if (value)
        sessionStorage.setItem(storageKey.current, JSON.stringify(value))
      else sessionStorage.removeItem(storageKey.current)
    } catch {
      /* In-memory retries still retain the exact request if storage is unavailable. */
    }
  }
  useEffect(() => {
    alive.current = true
    let current = true
    void hostFetch('/v1/apps/chronicle/catalog')
      .then((value: Catalog) => {
        if (!current) return
        setCatalog(value)
        setError('')
        storageKey.current = `bridge.chronicle.launch.${value.user.id}.${identity.state?.current_org?.id ?? record?.org_id ?? 'unbound'}.${app.digest ?? app.version}.${record?.id ?? `new.${startupKey}`}`
        try {
          const stored = sessionStorage.getItem(storageKey.current)
          const restored = stored ? (JSON.parse(stored) as LaunchAttempt) : null
          if (
            restored?.requestId &&
            restored.draft === true &&
            restored.config &&
            typeof restored.config === 'object' &&
            restored.config.startup_values
          ) {
            const compatible = {
              ...restored,
              project: restored.project ?? restored.config.project,
              environment: restored.environment ?? restored.config.environment,
              config: chronicleLaunchSettings(restored.config),
            }
            pendingLaunch.current = compatible
            if (JSON.stringify(compatible) !== stored) saveAttempt(compatible)
            setLaunchPending(true)
            setProviderSettings({
              ...compatible.config,
              project: compatible.project ?? compatible.config.project,
              environment:
                compatible.environment ?? compatible.config.environment,
            })
            setTimeoutMinutes(compatible.config.idle_timeout_minutes ?? 30)
          }
        } catch {
          /* A missing or invalid local retry receipt starts with the current catalog. */
        }
        const saved =
          pendingLaunch.current?.config.startup_values ??
          record?.config.startup_values
        setGitHubConnected(Array.isArray(saved?.account_ids))
        setScenario(
          typeof saved?.scenario_id === 'string' ? saved.scenario_id : null,
        )
        const initialAccounts = Array.isArray(saved?.account_ids)
          ? saved.account_ids.filter(
              (id): id is string => typeof id === 'string',
            )
          : []
        setAccounts(initialAccounts)
      })
      .catch((failure: Error) => {
        if (current) setError(failure.message)
      })
    return () => {
      current = false
      alive.current = false
    }
  }, [record?.id, attempt, identity.state?.current_org?.id, startupKey])

  const selected =
    catalog?.accounts.filter((account) => accounts.includes(account.id)) ?? []
  const locked =
    busy || launchPending || (!!draft && draft.status !== 'created')

  const start = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (
      !catalog ||
      !githubConnected ||
      !accounts.length ||
      !selection.available
    )
      return
    setBusy(true)
    setError('')
    try {
      const values = {
        account_ids: accounts,
        scenario_id:
          scenario ?? scenarios.catalog?.default_scenario ?? 'executive',
      }
      const settings = launchSettings({
        ...providerSettings,
        startup_values: values,
        provider: selection.provider?.id,
        execution_mode: selection.mode?.id,
        environment: selection.environment,
        idle_timeout_minutes: timeout,
      })
      const attempt: LaunchAttempt = pendingLaunch.current ?? {
        requestId: crypto.randomUUID(),
        packageDigest: app.digest,
        draft: true as const,
        project: settings.project,
        environment: settings.environment,
        // Chronicle accepts launch choices, not the full session defaults or identity.
        config: {
          protocol: settings.protocol,
          provider: settings.provider,
          execution_mode: settings.execution_mode,
          acp_auth_method: settings.acp_auth_method,
          acp_config_options: settings.acp_config_options,
          model: settings.model,
          effort: settings.effort,
          fast_mode: settings.fast_mode,
          idle_timeout_minutes: settings.idle_timeout_minutes,
          startup_step: settings.startup_step,
          startup_values: settings.startup_values,
          permission_mode: settings.permission_mode,
          max_turns: settings.max_turns,
          max_budget_usd: settings.max_budget_usd,
          schedule_id: settings.schedule_id,
        },
        ...(draft ? { sessionId: draft.id } : {}),
      }
      saveAttempt(attempt)
      let session = attempt.sessionId
        ? await api.session(attempt.sessionId)
        : draft
      if (!session) {
        const { sessionId: _sessionId, ...payload } = attempt
        const launched = await api.launchApp(app.id, payload)
        if (!launched.session)
          throw new Error('Chronicle could not save this analysis session.')
        session = launched.session
        saveAttempt({ ...attempt, sessionId: session.id })
        if (alive.current) setDraft(session)
      }
      const savedSessionId = session.id
      const continueStartup = async () => {
        let current = await api.session(savedSessionId)
        if (current.status === 'created') {
          const {
            protocol: _protocol,
            acp_version: _version,
            provider: _provider,
            execution_mode: _execution,
            environment: _environment,
            provider_session_id: _providerSession,
            ...savedSettings
          } = attempt.config
          current = await api.updateConfig(current.id, savedSettings)
          current = await api.startSession(current.id)
          if (alive.current) setDraft(current)
        }
        if (['asleep', 'closed'].includes(current.status)) {
          current = await api.resumeSession(current.id)
          if (alive.current) setDraft(current)
        }
        if (
          ['error', 'exited', 'detached', 'archived'].includes(current.status)
        )
          throw new Error(
            current.error
              ? `Chronicle could not start: ${current.error}`
              : 'The Chronicle session ended before analysis could begin. Start a new analysis.',
          )
        if (!current.config.app_binding_id)
          throw new Error(
            'Chronicle could not connect this session to its analysis workspace.',
          )
        await hostFetch(
          `/v1/apps/bindings/${encodeURIComponent(current.config.app_binding_id)}/chronicle/begin`,
          {},
        )
        if (alive.current) saveAttempt(null)
        else {
          try {
            sessionStorage.removeItem(storageKey.current)
          } catch {
            /* Storage is optional. */
          }
        }
      }
      if (onStarting) {
        onStarting(session, continueStartup)
        return
      }
      await continueStartup()
      if (alive.current) {
        onCreated(await api.session(session.id))
        onChanged()
      }
    } catch (failure) {
      if (alive.current) {
        if (
          failure instanceof ApiError &&
          [400, 422].includes(failure.status) &&
          !pendingLaunch.current?.sessionId
        )
          saveAttempt(null)
        setError((failure as Error).message)
      }
    } finally {
      if (alive.current) setBusy(false)
    }
  }

  return (
    <div className="startup-surface chronicle-startup-surface">
      <section className="chronicle-startup">
        <header className="chronicle-startup-brand">
          <img
            src={appIcon(app.iconUrl, 128)}
            width={40}
            height={40}
            decoding="async"
            alt=""
          />
          <span>Chronicle Code</span>
        </header>
        <form id={formId} onSubmit={(event) => void start(event)}>
          <div className="chronicle-onboarding-heading">
            <p className="label">NEW ANALYSIS</p>
            <h1>See the whole engineering picture.</h1>
            <p>
              Connect your GitHub organization. Chronicle brings its code,
              dependencies and delivery patterns into one evidence-backed
              review.
            </p>
          </div>
          {catalog && !githubConnected ? (
            <section
              className="chronicle-github-entry"
              aria-label="Connect GitHub"
            >
              <GitHubMark size={36} />
              <h2>Connect your GitHub account</h2>
              <p>
                Continue as {catalog.user.name} to choose the organization you
                want to understand.
              </p>
              <button
                type="button"
                className="btn chronicle-github-connect"
                onClick={() => setGitHubConnected(true)}
              >
                <GitHubMark size={18} />
                Connect to GitHub
              </button>
              <small>No GitHub sign-in required</small>
            </section>
          ) : catalog ? (
            <>
              <section
                className="chronicle-setup-section"
                aria-label="GitHub organizations"
              >
                <div className="chronicle-github-identity">
                  <GitHubMark size={30} />
                  <div>
                    <strong>GitHub</strong>
                    <p>
                      Signed in as <b>{catalog.user.name}</b> · @
                      {catalog.user.github_login}
                    </p>
                  </div>
                  <span>Repository connection</span>
                </div>
                <div className="chronicle-section-title">
                  <span>01</span>
                  <div>
                    <h2>Choose an organization to analyze</h2>
                    <p>
                      All repositories in the selected organization are
                      included.
                    </p>
                  </div>
                </div>
                <div className="chronicle-accounts">
                  {catalog.accounts.map((account) => (
                    <label
                      key={account.id}
                      className={`chronicle-account ${accounts.includes(account.id) ? 'connected' : ''}`}
                    >
                      <span
                        className="chronicle-account-avatar"
                        aria-hidden="true"
                      >
                        {account.name
                          .split(/\s+/)
                          .slice(0, 2)
                          .map((part) => part[0])
                          .join('')}
                      </span>
                      <span>
                        <strong>{account.name}</strong>
                        <small>@{account.login}</small>
                      </span>
                      <input
                        type="radio"
                        name="github-organization"
                        aria-label={account.name}
                        disabled={locked}
                        checked={accounts.includes(account.id)}
                        onChange={() => setAccounts([account.id])}
                      />
                    </label>
                  ))}
                </div>
              </section>
              <details className="startup-advanced chronicle-advanced">
                <summary>Analysis settings</summary>
                <ProviderSettings
                  config={config}
                  settings={providerSettings}
                  onChange={(patch) =>
                    setProviderSettings((current) => ({ ...current, ...patch }))
                  }
                  disabled={locked}
                  providerLocked
                />
                <div className="startup-fields">
                  <label>
                    Review perspective
                    <select
                      className="control w-full"
                      aria-label="Analysis scenario"
                      disabled={locked || !scenarios.catalog}
                      value={
                        scenario ??
                        scenarios.catalog?.default_scenario ??
                        'executive'
                      }
                      onChange={(event) => setScenario(event.target.value)}
                    >
                      {(
                        scenarios.catalog?.scenarios ?? [
                          {
                            id: 'executive',
                            title: 'Executive review',
                            description: '',
                          },
                        ]
                      ).map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.title}
                        </option>
                      ))}
                    </select>
                    <small>
                      {
                        scenarios.catalog?.scenarios.find(
                          (item) =>
                            item.id ===
                            (scenario ?? scenarios.catalog?.default_scenario),
                        )?.description
                      }
                    </small>
                    {scenarios.error && (
                      <small role="alert">
                        {scenarios.error}{' '}
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={scenarios.retry}
                        >
                          Try again
                        </button>
                      </small>
                    )}
                  </label>
                  <label>
                    Sleep after inactivity (minutes)
                    <input
                      type="number"
                      aria-label="Analysis sleep timeout"
                      min={1}
                      max={1440}
                      required
                      disabled={locked}
                      value={timeout}
                      onChange={(event) =>
                        setTimeoutMinutes(Number(event.target.value))
                      }
                    />
                  </label>
                </div>
              </details>
            </>
          ) : (
            !error && (
              <p role="status" className="chronicle-catalog-loading">
                <Icon name="loader" size={17} className="spin" />
                Loading your GitHub organizations…
              </p>
            )
          )}
        </form>
      </section>
      <div
        className="chronicle-startup-actions"
        hidden={!githubConnected && !error}
      >
        {error && (
          <p role="alert" className="chronicle-startup-error">
            {error}
            {launchPending &&
              ' Your selection is preserved. Retry to recover this analysis.'}
            {!catalog && (
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setAttempt((value) => value + 1)}
              >
                Try again
              </button>
            )}
          </p>
        )}
        <footer className="chronicle-startup-footer" hidden={!githubConnected}>
          <div aria-live="polite" aria-atomic="true">
            <strong>
              {selected.length}{' '}
              {selected.length === 1 ? 'organization' : 'organizations'}{' '}
              selected
            </strong>
            <span>
              {selected.length
                ? 'All repositories · 12 connected analyses'
                : 'Select an organization to begin'}
            </span>
          </div>
          <button
            type="submit"
            form={formId}
            className="btn btn-primary"
            disabled={
              busy ||
              !catalog ||
              !githubConnected ||
              !accounts.length ||
              !selection.available
            }
          >
            {busy ? (
              <>
                <Icon name="loader" size={15} className="spin" />
                Starting analysis…
              </>
            ) : (
              <>
                {draft && draft.status !== 'created'
                  ? 'Continue opening analysis'
                  : 'Start analysis'}
                <Icon name="chevronRight" size={15} />
              </>
            )}
          </button>
        </footer>
      </div>
      <footer
        className="session-status chrome"
        role="contentinfo"
        aria-label="Session status bar"
      >
        <span className="session-lifecycle">Setup</span>
        <SessionCost
          sessionId={draft?.id}
          total={draft?.total_cost_usd ?? 0}
          unreported={!draft}
        />
      </footer>
    </div>
  )
}
